/**
 * @fileoverview AR-670 — Google Classroom entry in the Arc project metadata
 * row (prototype: components/Projects/GoogleClassroomField.tsx). Replaces the
 * AR-805 ProjectClassroomCard. All gating lives here so a school without the
 * feature (or a student/guardian, or a template) never mounts the body — the
 * body fetches the link state on mount and that request would be pure waste.
 */

import { useTranslation } from 'react-i18next'
import { Group, Stack, Text } from '@mantine/core'

import { GoogleClassroomLogo } from 'components/GoogleClassroomLogo'
import { hasPermission } from 'hooks'
import { useClassroomIntegrationEnabled } from 'integrations/classroom'
import { IProjectNew } from 'interfaces'
import { ProjectClassroomFieldBody } from './ProjectClassroomFieldBody'

/** Arc application statuses with a class + students to link against. */
const ASSIGNED_STATUSES = ['approved', 'finished']

interface ProjectClassroomFieldProps {
  readonly project: IProjectNew
}

export function ProjectClassroomField({ project }: ProjectClassroomFieldProps) {
  const { t } = useTranslation()
  const { enabled } = useClassroomIntegrationEnabled()

  const visible = enabled && hasPermission(['tutor', 'manager']) && project.kind !== 'template'
  if (!visible) return null

  const header = (
    <Group spacing={4} noWrap>
      <GoogleClassroomLogo size={14} />
      <Text size='sm' color='dimmed'>
        {t('classroom.googleClassroomSectionTitle')}
      </Text>
    </Group>
  )

  // Prototype behavior: a not-yet-assigned project has no class to link, so
  // show the hint and skip the fetch entirely.
  if (!ASSIGNED_STATUSES.includes(project.status)) {
    return (
      <Stack spacing={2} miw={160} maw={300}>
        {header}
        <Text size='xs' color='dimmed'>
          {t('classroom.syncToGoogleClassroomNeedsAssignedProject')}
        </Text>
      </Stack>
    )
  }

  return <ProjectClassroomFieldBody projectId={project.id} header={header} />
}
